import React, { useEffect, useState } from 'react'
import { Col, Row, Progress, Card, Button, Modal, Input, message, Space } from 'antd'
import { format } from '../helpers/number'
import api from '../helpers/api'
import { toast } from 'react-toastify'
import { ApplicationState } from '../store'
import { useSelector } from 'react-redux'
import { Redirect } from 'react-router'
import useSWR from 'swr'
import { CopyOutlined } from '@ant-design/icons'

interface IDailyQuest {
    todayDeposit: number
    quests: {
        id: number
        targetAmount: number
        reward: number
        completed: boolean
        claimed: boolean
    }[]
}

const Quest = () => {
    const prefix: any = useSelector((state: ApplicationState) => state.prefix.info)
    const userState = useSelector((state: ApplicationState) => state.user)
    const [dailyQuest, setDailyQuest] = useState<IDailyQuest>({
        todayDeposit: 0,
        quests: [
            { id: 1, targetAmount: 10000, reward: 1000, completed: false, claimed: false },
            { id: 2, targetAmount: 25000, reward: 1000, completed: false, claimed: false },
            { id: 3, targetAmount: 75000, reward: 2000, completed: false, claimed: false },
            { id: 4, targetAmount: 100000, reward: 3000, completed: false, claimed: false }
        ]
    })
    const [couponModal, setCouponModal] = useState<{
        visible: boolean
        couponCode: string
        reward: number
    }>({
        visible: false,
        couponCode: '',
        reward: 0
    })

    const getDailyQuest = async () => {
        try {
            const res = await api.get<void, any>(`/member/deposit/today`)
            const depositAmount = res?.depositToday || 0

            // Update todayDeposit in dailyQuest state
            setDailyQuest(prev => ({
                ...prev,
                todayDeposit: depositAmount
            }))

            return depositAmount
        } catch ({ message }: any) {
            toast.error(message as string)
            return null
        }
    }

    const claimReward = async (questId: number) => {
        // Find the quest to get reward amount
        const quest = dailyQuest.quests.find(q => q.id === questId)
        if (!quest) return

        let couponCode = ""

        // Set coupon code based on target amount
        switch (quest.targetAmount) {
            case 10000:
                couponCode = "LXUXGS2VCROHVJIK23"
                break
            case 25000:
                couponCode = "F4AJOBFSB1RXSUWF24"
                break
            case 75000:
                couponCode = "ATALF3JJ3I20OD7M16"
                break
            case 100000:
                couponCode = "5WE967Z2BP8ODKKD17"
                break
            default:
                couponCode = "default"
        }

        setCouponModal({
            visible: true,
            couponCode: couponCode,
            reward: quest.reward
        })
    }

    const copyCouponCode = () => {
        navigator.clipboard.writeText(couponModal.couponCode)
        message.success('คัดลอกโค้ดคูปองแล้ว!')
    }

    const closeCouponModal = () => {
        setCouponModal({
            visible: false,
            couponCode: '',
            reward: 0
        })
    }

    const { data: questData } = useSWR([`/member/daily-quest`], () => getDailyQuest(), { refreshInterval: 30000 });

    useEffect(() => {
        if (questData && questData.quests) {
            setDailyQuest(questData)
        }
    }, [questData])

    const renderDailyQuest = () => {
        // Add safety check for quests array
        if (!dailyQuest.quests || !Array.isArray(dailyQuest.quests)) {
            return <div style={{ color: '#fff', textAlign: 'center', padding: 20 }}>กำลังโหลดข้อมูล...</div>
        }

        return (
            <>
                {dailyQuest.quests.map((quest) => {
                    const progress = Math.min((dailyQuest.todayDeposit / quest.targetAmount) * 100, 100)
                    const isCompleted = dailyQuest.todayDeposit >= quest.targetAmount

                    return (
                        <div key={quest.id} style={{ marginBottom: 16, padding: 16, backgroundColor: '#262626', borderRadius: 8 }}>
                            <Row justify="space-between" align="middle" style={{ marginBottom: 8 }}>
                                <Col>
                                    <div style={{ color: '#fff', fontSize: 24 }}>
                                        ฝากครบ {format(quest.targetAmount, 0)} บาท
                                    </div>
                                    <div style={{ color: '#52c41a', fontSize: 22 }}>
                                        รางวัล: {format(quest.reward, 0)} บาท
                                    </div>
                                </Col>
                                <Col>
                                    <Button
                                        type="primary"
                                        disabled={!isCompleted || quest.claimed}
                                        onClick={() => claimReward(quest.id)}
                                        style={{
                                            backgroundColor: quest.claimed ? '#666' : isCompleted ? '#52c41a' : '#666',
                                            borderColor: quest.claimed ? '#666' : isCompleted ? '#52c41a' : '#666'
                                        }}
                                    >
                                        {quest.claimed ? 'รับแล้ว' : isCompleted ? 'รับรางวัล' : 'ยังไม่ครบ'}
                                    </Button>
                                </Col>
                            </Row>

                            <Progress
                                percent={progress}
                                strokeColor={{
                                    '0%': '#108ee9',
                                    '100%': '#52c41a',
                                }}
                                trailColor="#444"
                                showInfo={true}
                                format={(percent) => `${Math.round(percent || 0)}%`}
                            />
                        </div>
                    )
                })}
            </>
        )
    }

    if (userState.info?.phone !== '0998217848') {
        return <Redirect to="wallet" />
    }

    return (
        <>
            <div style={{ color: '#fff', marginBottom: 8 }}>ยอดฝากวันนี้: {format(dailyQuest.todayDeposit, 0)} บาท</div>

            {/* Daily Quest Section */}
            {renderDailyQuest()}

            {/* Coupon Modal */}
            <Modal
                title="🎉 รับรางวัลสำเร็จ!"
                open={couponModal.visible}
                onCancel={closeCouponModal}
                footer={null}
                centered
            >
                <div>
                    <div style={{ marginBottom: 8, fontSize: 22 }}>
                        โค้ดเครดิต {format(couponModal.reward, 0)} บาท
                    </div>

                    <Space.Compact style={{ width: '100%' }}>
                        <Input value={couponModal.couponCode} style={{ paddingTop: 0, paddingBottom: 0 }} readOnly />
                        <Button type="primary" icon={<CopyOutlined />} onClick={copyCouponCode} />
                    </Space.Compact>
                </div>
            </Modal>
        </>
    )
}

export default Quest